<script lang="ts">
  import InspectorField from '../InspectorField.svelte';
  import ConfirmDialog from '../../ui/ConfirmDialog.svelte';
  import { patchLoad, deleteLoad, getErrorMessage } from '$lib/api';
  import { clearEntity } from '$lib/router';
  import { LOAD_TYPES } from '$lib/constants/load';
  import { loadsQuery } from '$lib/queries/loads';
  import { roomsQuery } from '$lib/queries/rooms';
  import { invalidateAfterLoadCreate, invalidateAfterLoadDelete } from '$lib/state/invalidation';
  import { router } from '$lib/router';

  let { id }: { id: number } = $props();

  const loads = loadsQuery();
  const rooms = roomsQuery();

  let load = $derived((loads.data ?? []).find((l) => l.id === id));

  let showDeleteConfirm = $state(false);
  let selectError = $state('');

  async function saveName(name: string) {
    await patchLoad(id, { name });
    invalidateAfterLoadCreate(router.unitId!);
  }

  async function saveDescription(description: string) {
    await patchLoad(id, { description });
    invalidateAfterLoadCreate(router.unitId!);
  }

  async function saveWattage(value: string) {
    const wattage = value === '' ? undefined : Number(value);
    await patchLoad(id, { wattage_per_unit: wattage });
    invalidateAfterLoadCreate(router.unitId!);
  }

  async function saveRoom(event: Event) {
    const roomId = Number((event.currentTarget as HTMLSelectElement).value);
    if (!roomId || roomId === load?.room) return;
    selectError = '';
    try {
      await patchLoad(id, { room: roomId });
      invalidateAfterLoadCreate(router.unitId!);
    } catch (e) {
      selectError = getErrorMessage(e) || 'Failed to move load';
    }
  }

  async function saveLoadType(event: Event) {
    const loadType = (event.currentTarget as HTMLSelectElement).value;
    if (!loadType || loadType === load?.load_type) return;
    selectError = '';
    try {
      await patchLoad(id, { load_type: loadType });
      invalidateAfterLoadCreate(router.unitId!);
    } catch (e) {
      selectError = getErrorMessage(e) || 'Failed to update load type';
    }
  }

  async function handleDelete() {
    showDeleteConfirm = false;
    if (!load) return;
    await deleteLoad(load.id);
    invalidateAfterLoadDelete(router.unitId!);
    clearEntity();
  }
</script>

{#if load}
  <div class="px-3 py-3 space-y-4">
    <div class="space-y-2">
      <InspectorField label="Name" value={load.name} onSave={saveName} />

      <div class="space-y-1">
        <label class="block text-2xs font-medium text-text-muted uppercase tracking-wide">
          Room
        </label>
        <select
          class="w-full px-2 py-1.5 text-sm border border-border-strong rounded bg-bg-canvas text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
          value={load.room}
          onchange={saveRoom}
        >
          {#each rooms.data ?? [] as room}
            <option value={room.id}>{room.name}</option>
          {/each}
        </select>
      </div>

      <div class="space-y-1">
        <label class="block text-2xs font-medium text-text-muted uppercase tracking-wide">
          Load type
        </label>
        <select
          class="w-full px-2 py-1.5 text-sm border border-border-strong rounded bg-bg-canvas text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
          value={load.load_type}
          onchange={saveLoadType}
        >
          {#each Object.entries(LOAD_TYPES) as [type, meta]}
            <option value={type}>{meta.label}</option>
          {/each}
        </select>
      </div>

      {#if selectError}
        <p class="text-2xs text-state-error">{selectError}</p>
      {/if}

      <InspectorField
        label="Wattage"
        type="number"
        value={load.wattage_per_unit != null ? String(load.wattage_per_unit) : ''}
        placeholder="W per unit"
        mono
        onSave={saveWattage}
      />

      <InspectorField
        label="Description"
        type="textarea"
        value={load.description || ''}
        placeholder="Load notes"
        onSave={saveDescription}
      />
    </div>

    <div class="pt-4 border-t border-border-default space-y-2">
      <p class="text-2xs font-medium text-text-muted uppercase tracking-wide">Danger zone</p>
      <button
        type="button"
        class="w-full px-2 py-1.5 text-sm text-state-error border border-state-error/30 rounded hover:bg-state-error/5 transition-colors duration-100"
        onclick={() => (showDeleteConfirm = true)}
      >
        Delete load
      </button>
    </div>
  </div>
{:else}
  <div class="px-3 py-3 text-sm text-text-muted">Load not found</div>
{/if}

<ConfirmDialog
  bind:open={showDeleteConfirm}
  title="Delete load"
  message="Delete “{load?.name}”? Channel assignments for this load will be removed."
  confirmText="Delete"
  onconfirm={handleDelete}
  oncancel={() => (showDeleteConfirm = false)}
/>
